import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEspecificacao } from '../contexts/EspecificacaoContext';
import { BotaoEspecificacao } from '../components/especificacao/EspecificacaoOverlays';
import { fmtQtd } from '../utils/especificacao';
import api from '../services/api';
import './Catalogo.css';

// Estoque com a unidade do produto: pisos = m², metais/louças = un/pç/etc.
const fmtEstoque = (v, unid) => {
  const n = Number(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
  const u = String(unid || '').toUpperCase() === 'M2' ? 'm²' : String(unid || 'un').toLowerCase();
  return `${n} ${u}`;
};

/**
 * Página de detalhe de um produto do catálogo (rota /catalogo/:codpro).
 * Estilo e-commerce: galeria à esquerda, ficha + estoque à direita, botão voltar.
 * Mostra estoque do grupo, destaque da Central-04 (CD) e estoque por loja.
 */
export default function ProdutoCatalogo() {
  const { codpro } = useParams();
  const navigate = useNavigate();
  const { apiUrl } = useAuth();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [fotoAtiva, setFotoAtiva] = useState(0);
  const [errosFoto, setErrosFoto] = useState({}); // índices cujo arquivo não existe (404)
  const [lightbox, setLightbox] = useState(false); // foto ampliada aberta
  const touchX = useRef(null);

  useEffect(() => {
    setCarregando(true);
    setErro('');
    setFotoAtiva(0);
    setErrosFoto({});
    setLightbox(false);
    window.scrollTo(0, 0);
    api.get(`/catalogo/${codpro}`)
      .then(r => setDados(r.data))
      .catch(e => setErro(e.response?.data?.error || 'Erro ao carregar o produto'))
      .finally(() => setCarregando(false));
  }, [codpro]);

  const voltar = () => navigate(-1);

  const fotos = dados?.fotos || [];
  const esp = useEspecificacao();
  const naEsp = esp?.habilitado && dados ? esp.itemDoProduto(dados.codpro) : null;
  const isEncomenda = dados?.disponibilidade === 'Encomenda';

  // Índices das fotos que carregaram (pra navegar no lightbox sem cair em 404)
  const fotosOk = fotos.map((_, i) => i).filter(i => !errosFoto[i]);
  const temFoto = fotosOk.includes(fotoAtiva);

  // Avança/volta circular entre as fotos válidas
  const navegar = useCallback((delta) => {
    setFotoAtiva(atual => {
      const ok = fotos.map((_, i) => i).filter(i => !errosFoto[i]);
      if (ok.length < 2) return atual;
      const pos = ok.indexOf(atual);
      return ok[(pos + delta + ok.length) % ok.length];
    });
  }, [fotos, errosFoto]);

  // Lightbox aberto: trava scroll do body + atalhos de teclado (ESC / setas)
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightbox(false);
      else if (e.key === 'ArrowLeft') navegar(-1);
      else if (e.key === 'ArrowRight') navegar(1);
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [lightbox, navegar]);

  // Swipe no celular pra trocar de foto dentro do lightbox
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 45) navegar(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div>
      <div className="cd-topo-acoes">
        <button className="cat-voltar" onClick={voltar}>
          <i className="bi bi-arrow-left" /> Voltar ao catálogo
        </button>
        <BotaoEspecificacao />
      </div>

      {carregando && (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>
      )}
      {erro && !carregando && <div className="alert alert-danger">{erro}</div>}

      {dados && !carregando && (
        <>
          {/* Cabeçalho */}
          <div className="mb-4">
            <div className="cd-page-tipo">{dados.tipo}</div>
            <h1 className="cd-page-nome">{dados.nome}</h1>
            <div className="cd-page-cod">Cód {dados.codpro} · {dados.fornecedor}</div>
            {dados.pagaComissao && (
              <span className="cd-comissao"><i className="bi bi-cash-coin" />Paga comissão por indicação</span>
            )}
          </div>

          <div className="row g-4">
            {/* Galeria */}
            <div className="col-12 col-md-4">
              <div
                className={`cd-page-galeria-main ${temFoto ? 'tem-foto' : ''}`}
                onClick={() => temFoto && setLightbox(true)}
              >
                {temFoto ? (
                  <>
                    <img
                      key={fotoAtiva}
                      src={`${apiUrl}${fotos[fotoAtiva] || fotos[0]}`}
                      alt={dados.nome}
                      onError={() => setErrosFoto(e => ({ ...e, [fotoAtiva]: true }))}
                    />
                    <div className="cd-zoom-hint" aria-hidden="true"><i className="bi bi-zoom-in" /></div>
                  </>
                ) : (
                  <div className="cd-galeria-ph"><i className="bi bi-box-seam" /></div>
                )}
              </div>
              {fotos.filter((_, i) => !errosFoto[i]).length > 1 && (
                <div className="cd-thumbs">
                  {fotos.map((f, i) => errosFoto[i] ? null : (
                    <button
                      key={f}
                      className={`cd-thumb ${i === fotoAtiva ? 'ativo' : ''}`}
                      onClick={() => setFotoAtiva(i)}
                      aria-label={`Foto ${i + 1}`}
                    >
                      <img src={`${apiUrl}${f}`} alt={`${dados.nome} ${i + 1}`} onError={() => setErrosFoto(e => ({ ...e, [i]: true }))} />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Ficha + estoque */}
            <div className="col-12 col-md-8">
              {/* Disponibilidade */}
              <div className="mb-3">
                <span className={`cat-badge cat-badge-xl ${isEncomenda ? 'cat-badge-encomenda' : 'cat-badge-linha'}`}>
                  <i className={`bi ${isEncomenda ? 'bi-clock-history' : 'bi-check-circle'}`} />
                  {dados.disponibilidade}
                </span>
              </div>

              {/* Especificar no projeto (carrinho do arquiteto) */}
              {esp?.habilitado && (
                <div className="cd-espec">
                  <button type="button" className="esp-btn cd-espec-btn"
                    onClick={() => esp.abrirAdicionar({ codpro: dados.codpro, nome: dados.nome, fornecedor: dados.fornecedor, unid: dados.unid, fotos })}>
                    <i className="bi bi-plus-lg" />{naEsp ? 'Especificar mais' : 'Especificar no projeto'}
                  </button>
                  {naEsp ? (
                    <div className="cd-espec-info">
                      <i className="bi bi-check2-circle" />
                      <span>Já está em <b>{esp.ativa.nome}</b>: {fmtQtd(naEsp.quantidade, naEsp.unidade)}{naEsp.ambientes.length ? ` · ${naEsp.ambientes.join(', ')}` : ''}</span>
                    </div>
                  ) : (
                    <div className="cd-espec-info leve">Monte a lista do projeto do seu cliente e gere o PDF quantitativo com a sua logo.</div>
                  )}
                </div>
              )}

              {/* Estoque total (todas as lojas, já incluindo o CD) */}
              <div className="cd-est-total">
                <div>
                  <div className="lbl">Estoque disponível</div>
                  <div className="sub">somando todas as lojas</div>
                </div>
                <div className="num">{fmtEstoque(dados.estoqueGrupo, dados.unid)}</div>
              </div>

              {/* Ficha */}
              <div className="cd-ficha-row"><span className="lbl">Tipo</span><span className="val">{dados.tipo}</span></div>
              <div className="cd-ficha-row"><span className="lbl">Fornecedor</span><span className="val">{dados.fornecedor}</span></div>
              <div className="cd-ficha-row"><span className="lbl">Disponibilidade</span><span className="val">{dados.disponibilidade}</span></div>
              <div className="cd-ficha-row"><span className="lbl">Unidade</span><span className="val">{dados.unid}</span></div>
            </div>

            {/* Estoque + mostruário por loja — tabela única: as duas respondem à
                mesma pergunta ("onde está esse piso?"), e o arquiteto precisa saber
                onde há PEÇA EXPOSTA pra levar o cliente pra ver. */}
            <div className="col-12">
              <h6 className="fw-bold mb-2" style={{ color: 'var(--primary-dark)', fontSize: '0.98rem' }}>
                <i className="bi bi-shop me-2" style={{ color: 'var(--primary)' }} />
                {dados.temMostruario ? 'Estoque e mostruário por loja' : 'Estoque por loja'}
              </h6>
              {/* Nota do CD: aqui embaixo, ao lado da lista onde a Central aparece */}
              <div className="cd-cd-nota">
                <i className="bi bi-truck" style={{ color: 'var(--accent)', fontSize: '1.1rem', marginTop: 1, flexShrink: 0 }} />
                <div>
                  O nosso <strong>Centro de Distribuição</strong> abastece <strong>qualquer loja</strong> —
                  esse estoque pode ser enviado pra onde for melhor pro cliente. Pode contar com
                  ele pra montar o projeto.
                </div>
              </div>
              <table className="cd-lojas-tab">
                <thead>
                  <tr>
                    <th>Loja</th>
                    <th className="num">Estoque disponível</th>
                    {dados.temMostruario && <th className="cen">Peça em mostruário</th>}
                  </tr>
                </thead>
                <tbody>
                  {(dados.lojas || []).map(l => (
                    <tr key={l.filial} className={l.isCD ? 'cd' : ''}>
                      <td className="loja">
                        {l.isCD && <i className="bi bi-truck" style={{ color: '#9a6612', marginRight: 6 }} />}
                        <span style={l.isCD ? { color: '#9a6612', fontWeight: 700 } : undefined}>
                          {l.isCD ? 'Centro de Distribuição' : l.nome}
                        </span>
                      </td>
                      <td className={`num ${l.disponivel > 0 ? 'tem' : 'zero'}`}>{fmtEstoque(l.disponivel, dados.unid)}</td>
                      {/* Mostruário só pra pisos/revestimentos (0101) — nos outros departamentos a coluna some */}
                      {dados.temMostruario && (
                        <td className="cen">
                          {/* null = loja sem showroom próprio (o CD). Diferente de
                              "não tem": ali não existe o que expor. */}
                          {l.mostruario === null
                            ? <span className="most na" title="Esta loja não tem showroom próprio">—</span>
                            : l.mostruario
                              ? <span className="most sim"><i className="bi bi-check-circle-fill" /> tem</span>
                              : <span className="most nao"><i className="bi bi-x-circle-fill" /> não tem</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Lightbox: foto ampliada com navegação (clique fora ou ESC fecha) */}
          {lightbox && temFoto && (
            <div className="cd-lightbox" onClick={() => setLightbox(false)} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
              <button className="cd-lb-fechar" onClick={() => setLightbox(false)} aria-label="Fechar"><i className="bi bi-x-lg" /></button>
              {fotosOk.length > 1 && (
                <button className="cd-lb-nav prev" onClick={(e) => { e.stopPropagation(); navegar(-1); }} aria-label="Foto anterior"><i className="bi bi-chevron-left" /></button>
              )}
              <img className="cd-lb-img" src={`${apiUrl}${fotos[fotoAtiva]}`} alt={dados.nome} onClick={(e) => e.stopPropagation()} />
              {fotosOk.length > 1 && (
                <button className="cd-lb-nav next" onClick={(e) => { e.stopPropagation(); navegar(1); }} aria-label="Próxima foto"><i className="bi bi-chevron-right" /></button>
              )}
              {fotosOk.length > 1 && (
                <div className="cd-lb-contador">{fotosOk.indexOf(fotoAtiva) + 1} / {fotosOk.length}</div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
